import React from 'react';
import DataVisualisationCellContents from '@components/table/sub-components/data-visualisation-cell-contents';
import DynamicFilterBlock from '@components/dynamic-filter-block/dynamic-filter-block';
import NoResultsFound from '@components/global-commitment/no-results-found/no-results-found';
import OrganisationNameCell from '@components/table/sub-components/organisation-name-cell';
import styled from '@emotion/styled/macro';
import Table from '@components/table/table';
import TableCell from '@components/table/sub-components/table-cell';
import TableRow from '@components/table/sub-components/table-row';
import useFilter from '@hooks/use-filter';
import vars from '@variables';
import {
  convertStringToBoolean,
  convertStringToFloat,
  formatNumberWithCommas,
  generateKey,
  getPercentageValue,
} from '@utils/utils';
import { csrData as tableData } from '../../data/global-commitment-report-2021/listing-page.data';
import { generateGCReportUrl } from '../../utils/generate-url-functions';
import { ListingtableProps } from './global-commitment-table.types';
import { useStaticQuery, graphql } from 'gatsby';

const CsrListingTable: React.FC<ListingtableProps> = ({ className }) => {
  const data = useStaticQuery(graphql`
    query AllCsrQuery {
      csr: allGcCsrDataCsv {
        nodes {
          id
          category: category__b_0
          slug: slug__b_4
          name: organisation_name__b_1
          sortableName: organisation_name_sortable__b_1_bis
          publishReport: publish_report__b_3
          isFirstYear: first_year__rep
          afteruseActivities: afteruse_activities__16_1
          plasticCollected: plastic_collected__metric_tonnes____18_1_4
          plasticCollectedAltText: plastic_collected__alternative___18_4_1
          plasticSorted: plastic_sorted__metric_tonnes___18_1_3
          plasticSortedAltText: plastic_sorted__alternative___18_4_2
          plasticRecycled: _2020_plastic_recycled__metric_tonnes___18_1_1
          plasticRecycledWholeCellAltText: _2020_plastic_recycled__whole_cell_alt_text__18_1_alt
          plasticRecycledAltText: _2020_plastic_recycled__alternative___18_4_3
          plasticRecycledTarget: target_plastic_recycled__metric_tonnes___au_target
          plasticRecycledTargetAltText: target_plastic_recycled__alternative__au_target_excep
        }
      }
    }
  `);
  const allOrganisationData = data.csr.nodes;
  const { dynamicFilterBlockData, tableHeaderData } = tableData;
  const {
    filteredData,
    updateQueryArray,
    setCompareFunctionConfig,
  } = useFilter(allOrganisationData);

  return (
    <div className={className}>
      <DynamicFilterBlock
        updateQueryArray={updateQueryArray}
        setCompareFunctionConfig={setCompareFunctionConfig}
        elementData={dynamicFilterBlockData}
      />
      {filteredData.length > 0 ? (
        <div className="table-container">
          <Table tableHeaderData={tableHeaderData}>
            {filteredData.map((organisation, index) => {
              return (
                <TableRow
                  key={generateKey(organisation.name, index)}
                  url={
                    convertStringToBoolean(organisation.publishReport)
                      ? generateGCReportUrl(
                          organisation.category,
                          organisation.name,
                          organisation.slug
                        )
                      : null
                  }
                >
                  <OrganisationNameCell
                    name={organisation.name}
                    sector={organisation.sector}
                    tag={
                      convertStringToBoolean(organisation.isFirstYear)
                        ? {
                            text: 'first year',
                            colour: vars.plasticsBlue,
                          }
                        : null
                    }
                  />
                  <TableCell>{organisation.afteruseActivities}</TableCell>
                  <TableCell>
                    {organisation.plasticCollected
                      ? formatNumberWithCommas(organisation.plasticCollected)
                      : organisation.plasticCollectedAltText}
                  </TableCell>
                  <TableCell>
                    {organisation.plasticSorted
                      ? formatNumberWithCommas(organisation.plasticSorted)
                      : organisation.plasticSortedAltText}
                  </TableCell>
                  <TableCell>
                    {organisation.plasticRecycledWholeCellAltText ? (
                      organisation.plasticRecycledWholeCellAltText
                    ) : (
                      <DataVisualisationCellContents
                        currentValue={
                          organisation.plasticRecycled &&
                          organisation.plasticRecycledTarget
                            ? getPercentageValue(
                                convertStringToFloat(
                                  organisation.plasticRecycled
                                ),
                                convertStringToFloat(
                                  organisation.plasticRecycledTarget
                                )
                              )
                            : null
                        }
                        displayCurrentValue={
                          organisation.plasticRecycled
                            ? `${formatNumberWithCommas(
                                organisation.plasticRecycled
                              )} metric tonnes`
                            : organisation.plasticRecycledAltText
                        }
                        showPercentageChange={false}
                        target={100}
                        displayTarget={
                          organisation.plasticRecycledTarget
                            ? `Target: ${formatNumberWithCommas(
                                organisation.plasticRecycledTarget
                              )} metric tonnes`
                            : organisation.plasticRecycledTargetAltText
                        }
                        isFirstYear={convertStringToBoolean(
                          organisation.isFirstYear
                        )}
                        displayGraph={
                          organisation.plasticRecycled &&
                          organisation.plasticRecycledTarget
                            ? true
                            : false
                        }
                      />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </Table>
        </div>
      ) : (
        <NoResultsFound />
      )}
    </div>
  );
};

const StyledCsrListingTable = styled(CsrListingTable)`
  .table-container {
    overflow-x: auto;
    ${Table} {
      width: ${vars.contentMaxWidth};
    }
  }
`;

export default StyledCsrListingTable;
